import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  AlertTitle,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import { format } from 'date-fns';
import { useKpiCalculationStatusQuery, useRecalculateCompanyKpisMutation } from '../../gql/generated/graphql';
import { useSnackbar } from '../../providers/SnackbarProvider';

interface KPIStatusModalProps {
  open: boolean;
  onClose: () => void;
  companyId: number;
  companyName?: string;
}

const KPIStatusModal: React.FC<KPIStatusModalProps> = ({ open, onClose, companyId, companyName }) => {
  const { showSnackbar } = useSnackbar();
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [recalculationType, setRecalculationType] = useState<'all' | 'financial'>('all');

  const parsedCompanyId = companyId ? (typeof companyId === 'string' ? parseInt(companyId, 10) : companyId) : 0;

  const { data, loading, error, refetch } = useKpiCalculationStatusQuery({
    variables: { companyId: parsedCompanyId },
    skip: !open || !parsedCompanyId,
    fetchPolicy: 'network-only',
  });

  const kpiStatus = data?.kpiCalculationStatus;

  // Debug logging
  React.useEffect(() => {
    if (open) {
      console.log('KPIStatusModal Query Debug:', {
        open,
        companyId,
        parsedId: parsedCompanyId,
        skipCondition: !open || !parsedCompanyId,
        loading,
        error,
        hasData: !!data,
        data,
        kpiCalculationStatus: kpiStatus,
      });
    }
  }, [open, companyId, parsedCompanyId, loading, error, data, kpiStatus]);

  const [recalculateKpis] = useRecalculateCompanyKpisMutation({
    onCompleted: () => {
      showSnackbar('KPI recalculation started successfully', 'success');
      setIsRecalculating(false);
      // Refetch status after a delay
      setTimeout(() => {
        refetch();
      }, 2000);
    },
    onError: error => {
      showSnackbar(`Failed to recalculate KPIs: ${error.message}`, 'error');
      setIsRecalculating(false);
    },
  });

  const handleRecalculate = async (type: 'all' | 'financial' = 'all') => {
    setIsRecalculating(true);
    setRecalculationType(type);

    await recalculateKpis({
      variables: {
        companyId,
        // onlyCreateNew: false,
        // financialOnly: type === 'financial',
      },
    });
  };

  const getStatusIcon = (kpiStatus: any) => {
    if (!kpiStatus.isCalculated) {
      return <ErrorIcon color="error" fontSize="small" />;
    }
    if (!kpiStatus.isUpToDate) {
      return <WarningIcon color="warning" fontSize="small" />;
    }
    return <CheckCircleIcon color="success" fontSize="small" />;
  };

  const getStatusChip = (kpiStatus: any) => {
    if (!kpiStatus.isCalculated) {
      return <Chip label="Not Calculated" color="error" size="small" />;
    }
    if (!kpiStatus.isUpToDate) {
      return <Chip label="Outdated" color="warning" size="small" />;
    }
    return <Chip label="Up to Date" color="success" size="small" />;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    try {
      return format(new Date(dateString), 'dd MMM yyyy HH:mm');
    } catch {
      return 'Invalid Date';
    }
  };

  const needsRecalculation = kpiStatus?.needsRecalculation || false;

  // Calculate progress percentage
  const progressPercentage = kpiStatus ? (kpiStatus.calculatedKpis / kpiStatus.totalKpis) * 100 : 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">KPI Calculation Status</Typography>
            {companyName && (
              <Typography variant="subtitle2" color="text.secondary">
                {companyName}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error">
            <AlertTitle>Error</AlertTitle>
            Failed to load KPI status: {error.message}
          </Alert>
        )}

        {!loading && !error && !kpiStatus && (
          <Alert severity="info">
            <AlertTitle>No KPI Data Available</AlertTitle>
            KPI calculation status data is not available for this company. Please ensure KPIs have been calculated at
            least once.
          </Alert>
        )}

        {kpiStatus && (
          <>
            {/* Summary Section */}
            <Box mb={3}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Summary
                </Typography>

                <Box display="flex" gap={3} mb={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Last KPI Calculation
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {formatDate(kpiStatus.lastKpiCalculation)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Last Data Upload
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {formatDate(kpiStatus.lastDataUpload)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      KPIs Calculated
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {kpiStatus.calculatedKpis} / {kpiStatus.totalKpis}
                    </Typography>
                  </Box>
                </Box>

                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Overall Progress
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={progressPercentage}
                    sx={{ height: 8, borderRadius: 1 }}
                    color={progressPercentage === 100 ? 'success' : 'primary'}
                  />
                </Box>

                <Box display="flex" gap={2} flexWrap="wrap">
                  <Chip
                    icon={<CheckCircleIcon />}
                    label={`${kpiStatus.calculatedKpis} Calculated`}
                    color="success"
                    variant="outlined"
                    size="small"
                  />
                  {kpiStatus.outdatedKpis > 0 && (
                    <Chip
                      icon={<WarningIcon />}
                      label={`${kpiStatus.outdatedKpis} Outdated`}
                      color="warning"
                      variant="outlined"
                      size="small"
                    />
                  )}
                  {kpiStatus.missingDataKpis > 0 && (
                    <Chip
                      icon={<ErrorIcon />}
                      label={`${kpiStatus.missingDataKpis} Missing Data`}
                      color="error"
                      variant="outlined"
                      size="small"
                    />
                  )}
                </Box>

                {needsRecalculation && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <AlertTitle>Recalculation Recommended</AlertTitle>
                    The precalculated KPIs are not reflecting the current state of available data. Please trigger a
                    recalculation to update them.
                  </Alert>
                )}
              </Paper>
            </Box>

            {/* Financial KPIs Table */}
            <Box mb={3}>
              <Typography variant="h6" gutterBottom>
                Financial KPIs
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Based on uploaded benchmark data (Excel files)
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: '5%' }}>Status</TableCell>
                      <TableCell sx={{ width: '20%' }}>KPI Name</TableCell>
                      <TableCell align="right" sx={{ width: '10%' }}>
                        Current Value
                      </TableCell>
                      <TableCell sx={{ width: '15%' }}>Last Calculated</TableCell>
                      <TableCell sx={{ width: '12%' }}>Available Years</TableCell>
                      <TableCell sx={{ width: '25%' }}>Data Sources</TableCell>
                      <TableCell sx={{ width: '13%' }}>Issues</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {kpiStatus.kpiStatuses
                      ?.filter(kpi => kpi.kpiType === 'financial')
                      ?.map(kpi => (
                        <TableRow key={kpi.kpiName}>
                          <TableCell>
                            <Tooltip
                              title={
                                !kpi.isCalculated
                                  ? 'Not calculated'
                                  : !kpi.isUpToDate
                                    ? 'Outdated - needs recalculation'
                                    : 'Up to date'
                              }
                            >
                              <Box>{getStatusIcon(kpi)}</Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {kpi.displayName}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {kpi.value !== null && kpi.value !== undefined ? (
                              <Typography variant="body2">
                                {typeof kpi.value === 'number' ? kpi.value.toFixed(2) : kpi.value}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                N/A
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {kpi.lastCalculated ? formatDate(kpi.lastCalculated) : 'Never'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {kpi.dataAvailability?.availableYears?.length > 0 ? (
                              <Typography variant="body2">{kpi.dataAvailability.availableYears.join(', ')}</Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                None
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {kpi.dataAvailability?.dataSources?.length > 0 ? (
                              <Box display="flex" gap={0.5} flexWrap="wrap">
                                {kpi.dataAvailability.dataSources.map(source => (
                                  <Chip
                                    key={source}
                                    label={source}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      fontSize: '0.75rem',
                                      height: '24px',
                                      '& .MuiChip-label': {
                                        px: 1,
                                      },
                                    }}
                                  />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                None
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {!kpi.isCalculated && kpi.requiredDataMissing?.length > 0 ? (
                              <Tooltip title={kpi.requiredDataMissing.join(', ')}>
                                <Chip label="Missing Data" color="error" size="small" icon={<InfoIcon />} />
                              </Tooltip>
                            ) : !kpi.isUpToDate && kpi.isCalculated ? (
                              <Chip label="Outdated" color="warning" size="small" />
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Non-Financial KPIs Table */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Non-Financial KPIs
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Based on crawl data, LinkedIn profiles, and online visibility
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: '5%' }}>Status</TableCell>
                      <TableCell sx={{ width: '25%' }}>KPI Name</TableCell>
                      <TableCell align="right" sx={{ width: '12%' }}>
                        Current Value
                      </TableCell>
                      <TableCell sx={{ width: '18%' }}>Last Calculated</TableCell>
                      <TableCell sx={{ width: '25%' }}>Data Source</TableCell>
                      <TableCell sx={{ width: '15%' }}>Issues</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {kpiStatus.kpiStatuses
                      ?.filter(kpi => kpi.kpiType === 'non_financial')
                      ?.map(kpi => (
                        <TableRow key={kpi.kpiName}>
                          <TableCell>
                            <Tooltip title={!kpi.isCalculated ? 'Not calculated' : 'Up to date'}>
                              <Box>{getStatusIcon(kpi)}</Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {kpi.displayName}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {kpi.value !== null && kpi.value !== undefined ? (
                              <Typography variant="body2">
                                {typeof kpi.value === 'number' ? kpi.value.toFixed(2) : kpi.value}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                N/A
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {kpi.lastCalculated ? formatDate(kpi.lastCalculated) : 'Never'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {
                                // kpi.requiredDataMissing?.length > 0 ? kpi.requiredDataMissing[0] :
                                'Crawl/API data'
                              }
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {!kpi.isCalculated && kpi.requiredDataMissing?.length > 0 ? (
                              <Tooltip title={kpi.requiredDataMissing.join(', ')}>
                                <Chip label="Not Calculated" color="error" size="small" icon={<InfoIcon />} />
                              </Tooltip>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {needsRecalculation && (
          <>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={() => handleRecalculate('financial')}
              disabled={isRecalculating || loading}
            >
              {isRecalculating && recalculationType === 'financial'
                ? 'Recalculating Financial...'
                : 'Recalculate Financial KPIs'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={() => handleRecalculate('all')}
              disabled={isRecalculating || loading}
            >
              {isRecalculating && recalculationType === 'all' ? 'Recalculating All...' : 'Recalculate All KPIs'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default KPIStatusModal;
